import dayjs from "dayjs";
import { getData } from "../api/services";
import { dateFormat, defaultDateRangeDays, negativeSentimentColor, neutralSentimentColor, positiveSentimentColor, remainderSentimentColor } from "./constant";


export const getDefaultDateRange = () => {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD format
    // before 30 days
    const priorDate = new Date();
    priorDate.setDate(priorDate.getDate() - parseInt(defaultDateRangeDays));
    const priorDateString = priorDate.toISOString().slice(0, 10);
    return [dayjs(priorDateString, dateFormat), dayjs(today, dateFormat)];
}

export const fetchReviewsData = async (selectedCountry = "", selectedLocation = "", selectedCompany = "", selectedSource = "", selectedDate = getDefaultDateRange(), setReviewsData) => {
    try {
        let params = {};
        if (selectedCountry) {
            params = { country: selectedCountry };
        }
        if (selectedLocation) {
            params = { ...params, location: selectedLocation };
        }
        if (selectedCompany) {
            params = { ...params, brand: selectedCompany };
        }
        if (selectedSource) {
            params = { ...params, vendor_source: selectedSource };
        }
        if (selectedDate && selectedDate?.length === 2) {
            params = { ...params, startDate: selectedDate[0].format('YYYY-MM-DD'), endDate: selectedDate[1].format('YYYY-MM-DD') };
        }
        const data = await getData("/get-reviews-analysis-and-sentiments", params);
        setReviewsData(data);
    } catch (err) {
        console.error("Failed to fetch reviews", err);
    }
};

export const fetchSentimentAnalyticsData = async (selectedCountry = "", selectedLocation = "", selectedCompany = "", selectedSource = "", selectedDate = getDefaultDateRange(), setSentimentAnalyticsData) => {
    try {
        let params = {};
        if (selectedCountry) {
            params = { country: selectedCountry };
        }
        if (selectedLocation) {
            params = { ...params, location: selectedLocation };
        }
        if (selectedCompany) {
            params = { ...params, brand: selectedCompany };
        }
        if (selectedSource) {
            params = { ...params, vendor_source: selectedSource };
        }
        if (selectedDate && selectedDate?.length === 2) {
            let startDate = selectedDate[0];
            let endDate = selectedDate[1];
            if (!verifyDateFormat(startDate)) {
                startDate = selectedDate[0].format('YYYY-MM-DD');
            }
            if (!verifyDateFormat(endDate)) {
                endDate = selectedDate[1].format('YYYY-MM-DD');
            }
            params = { ...params, startDate, endDate };
        }
        const data = await getData(`/get-sentiment-analytics`, params);
        setSentimentAnalyticsData(data)
    } catch (error) {
        console.error("Failed to fetch sentiment analytics", error);

    }
}

export const handleExportClick = (exportType, data = [], filename = 'export') => {
    if (!exportType || !data.length) return;
    const now = new Date();
    const dateStr = now.toISOString().replace(/[:.-]/g, '').slice(0, 19);
    if (exportType === 'csv') {
        const _fileName = `${filename}_${dateStr}.csv`;
        jsonToCsvExport(data, _fileName);
    }
}

// 1) Flatten an object: { a:1, b:{ c:2 }, d:[x,y] } -> { a:1, "b.c":2, "d": "x | y" }
function flattenRow(obj, prefix = "", out = {}) {
    for (const [key, val] of Object.entries(obj ?? {})) {
        const k = prefix ? `${prefix}.${key}` : key;

        if (val === null || val === undefined) {
            out[k] = "";
        } else if (Array.isArray(val)) {
            // If array of primitives -> join; if array of objects -> JSON string
            const isPrimitiveArray = val.every(
                (v) => v === null || ["string", "number", "boolean"].includes(typeof v)
            );
            out[k] = isPrimitiveArray ? val.join(", ") : JSON.stringify(val);
        } else if (typeof val === "object") {
            flattenRow(val, k, out);
        } else {
            out[k] = val;
        }
    }
    return out;
}

// 2) Proper CSV escape: wrap in quotes and double internal quotes
function csvEscape(value) {
    const s = String(value ?? "");
    // Always quote to be safe (commas, newlines, quotes)
    return `"${s.replace(/"/g, '""')}"`;
}

// 3) Build CSV from JSON rows (auto-flattens nested props)
export function jsonToCsvExport(data, filename = "export.csv") {
    if (!Array.isArray(data) || data.length === 0) {
        console.error("No data available for export");
        return;
    }

    // Flatten all rows and collect headers
    const flattened = data.map((row) => {
        // Put your nested object under dot keys, e.g. sentimentDetails.*
        // This works generically for all nested properties
        return flattenRow(row);
    });

    // Build ordered header list (stable, predictable)
    const headerSet = new Set();
    for (const row of flattened) {
        Object.keys(row).forEach((k) => headerSet.add(k));
    }
    // Optional: ensure a nicer order (put common top-level fields first)
    const preferredOrder = [
        "_id",
        "vendor_source_id",
        "review_date",
        "review_title",
        "review_text",
        "stars",
        "sentimentDetails.sentiment",
        "sentimentDetails.confidence",
        "sentimentDetails.category",
        "sentimentDetails.keywords",
    ];
    const allHeaders = Array.from(headerSet);
    const headers = [
        ...preferredOrder.filter((h) => headerSet.has(h)),
        ...allHeaders.filter((h) => !preferredOrder.includes(h)).sort(),
    ];

    // Assemble CSV
    const rows = [];
    rows.push(headers.map(csvEscape).join(","));
    for (const row of flattened) {
        const line = headers
            .map((h) => csvEscape(row[h] ?? "")) // missing keys -> empty cell
            .join(",");
        rows.push(line);
    }

    const csvString = rows.join("\n");

    // Download in browser
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

export const verifyDateFormat = (date) => {
    if (!date) return false;
    // yyyy-mm-dd
    const regex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
    return regex.test(date);
}

export const getSentimentColor = (sentiment) => {
    switch (sentiment) {
        case "positive":
            return positiveSentimentColor; // green
        case "negative":
            return negativeSentimentColor; // red
        case "neutral":
            return neutralSentimentColor; // gray
        default:
            return remainderSentimentColor; // fallback dark
    }
};